import { isRouteErrorResponse, useRouteError } from 'react-router'
import {
  ErrorPage,
  ErrorPageActions,
  ErrorPageColumns,
  ErrorPageDescription,
  ErrorPageDetails,
  ErrorPageDetailsColumn,
  ErrorPageIllustrationColumn,
  ErrorPageKicker,
  ErrorPageTitle,
} from './ErrorPage'
import ErrorTechnicalBreakIllustration from '@/components/SpotIllustration/ErrorTechnicalBreakIllustration'
import { Button } from '@/components/Button'
import ErrorPageCrashedIllustration from '@/components/SpotIllustration/ErrorPageCrashedIllustration'
import ErrorPageNotFoundIllustration from '@/components/SpotIllustration/ErrorPageNotFoundIllustration'
import { externalLinks, links } from '@/utils/link'
import { useFeatures } from '@/contexts/root-store-context'
import { useObservableValue } from '@/hooks/rx-hooks'
import { observer } from 'mobx-react-lite'
import type { ReactNode } from 'react'
import { useLightDarkFn } from '@/features/shell/theming/color-scheme-hooks'

interface ErrorPageContainerProps {
  /**
   * The error.
   */
  error: unknown
}

/**
 * Error Page Container
 */
export const ErrorPageContainer = observer(function ErrorPageContainer({
  error,
}: ErrorPageContainerProps) {
  // We'll need to log the caught error, otherwise it won't be surfaced in the console.
  console.error(error)
  return (
    <BrandedErrorPageContainer>
      <ErrorPageColumns>
        <ErrorPageDetailsColumn>
          <ErrorPageKicker>Oh no!</ErrorPageKicker>
          <ErrorPageTitle>Page crashed</ErrorPageTitle>
          <ErrorPageDescription>
            Something went wrong while displaying this page. Try reloading, or
            navigate to another page to continue.
          </ErrorPageDescription>
          <ErrorPageActions>
            <Button href={links.home()} label="Go back home" />
            <Button
              label="Refresh"
              variant="outline"
              iconLeft="refresh"
              onPress={() => location.reload()}
            />
          </ErrorPageActions>
        </ErrorPageDetailsColumn>
        <ErrorPageIllustrationColumn>
          <ErrorPageCrashedIllustration size="lg" />
        </ErrorPageIllustrationColumn>
      </ErrorPageColumns>
      <ErrorPageDetails
        errorText={
          typeof error === 'object' && error && 'stack' in error && error.stack
            ? error.stack.toString()
            : '(no error details available)'
        }
      />
    </BrandedErrorPageContainer>
  )
})

/**
 * Technical Break Page
 */
export function TechnicalBreakPage() {
  return (
    <BrandedErrorPageContainer>
      <ErrorPageColumns>
        <ErrorPageDetailsColumn>
          <ErrorPageKicker>Hang tight!</ErrorPageKicker>
          <ErrorPageTitle>
            We’re offline, but we'll be back online soon!
          </ErrorPageTitle>
          <ErrorPageDescription>
            We apologize for the interruptions, but we are currently down for
            maintenance. We will be back online as soon as possible.
          </ErrorPageDescription>
          <ErrorPageActions>
            <Button
              label="Refresh"
              iconLeft="refresh"
              onPress={() => location.reload()}
            />
            <Button
              target="_blank"
              href="https://intercom.help/taxfyle"
              variant="outline"
              label="Visit our help center"
            />
          </ErrorPageActions>
        </ErrorPageDetailsColumn>
        <ErrorPageIllustrationColumn>
          <ErrorTechnicalBreakIllustration size="lg" />
        </ErrorPageIllustrationColumn>
      </ErrorPageColumns>
    </BrandedErrorPageContainer>
  )
}

/**
 * React Router error boundary.
 */
export function RouterErrorBoundary() {
  const error = useRouteError()
  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <BrandedErrorPageContainer>
        <ErrorPageColumns>
          <ErrorPageDetailsColumn>
            <ErrorPageKicker>404</ErrorPageKicker>
            <ErrorPageTitle>
              Oops, the page you're looking for does not exist!
            </ErrorPageTitle>
            <ErrorPageDescription>
              You may want to head back home. If you think something is broken,
              please contact support.
            </ErrorPageDescription>
            <ErrorPageActions>
              <Button href={links.home()} label="Go back home" />
              <Button
                href={externalLinks.contactSupport()}
                target="_blank"
                label="Contact support"
                variant="outline"
              />
            </ErrorPageActions>
          </ErrorPageDetailsColumn>
          <ErrorPageIllustrationColumn>
            <ErrorPageNotFoundIllustration size="lg" />
          </ErrorPageIllustrationColumn>
        </ErrorPageColumns>
      </BrandedErrorPageContainer>
    )
  }

  return <ErrorPageContainer error={error} />
}

interface BrandedErrorPageContainerProps {
  children: ReactNode
}

/**
 * Branded Error Page Container
 */
const BrandedErrorPageContainer = observer(function BrandedErrorPageContainer({
  children,
}: BrandedErrorPageContainerProps) {
  const lightDark = useLightDarkFn()
  const { readPreselectedWorkspaceInfo, currentWorkspace$ } = useFeatures(
    ({ iam }) => ({
      readPreselectedWorkspaceInfo: iam.readPreselectedWorkspaceInfo,
      currentWorkspace$: iam.currentWorkspace$,
    }),
  )

  const preselectedWorkspaceInfo = readPreselectedWorkspaceInfo()
  const workspace = useObservableValue(currentWorkspace$, {
    initial: null,
  })

  const logoUrl =
    workspace?.branding.logos.icon || preselectedWorkspaceInfo?.iconUrl
  const invertedLogoUrl =
    workspace?.branding.logos.iconWhite ||
    preselectedWorkspaceInfo?.invertedIconUrl
  const workspaceName = workspace?.name || preselectedWorkspaceInfo?.name

  const effectiveLogoUrl = lightDark(logoUrl, invertedLogoUrl)

  return (
    <ErrorPage logoUrl={effectiveLogoUrl} workspaceName={workspaceName}>
      {children}
    </ErrorPage>
  )
})
